import I18n from 'I18n';
import { useEffect } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import debounce from 'react-utils/debounce';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import useNavigate from '../../../hooks/useNavigate';
import { useFullPlaylist } from '../../savedItems/savedItemsQueries';
import { useCanUserEditPlaylist } from '../../savedItems/savedItemsHooks';
import { usePlaylistItemManager } from './PlaylistItemManager';
import produce from 'immer';
import { PLAYLIST_RESULTS_URL_FRAGMENT, RESULTS_URL_FRAGMENT } from '../../../constants/Navigation';
import UsageTracker from '../../../utils/UsageTracker';
import { usePlaylistItemsSearch } from '../playlistItemsHooks';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { View } from 'foundations-components/transitional/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Button } from 'foundations-components/transitional/components/Button';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SEARCH_QUERY_KEY = FILTER_TYPES.SEARCH;
export const PLAYLIST_ITEM_SEARCH_PATH = RESULTS_URL_FRAGMENT;
const noop = () => {};
const PlaylistItemSearchContext = /*#__PURE__*/createContext({
  query: new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '',
  resetQuery: noop,
  setQueryAndNavigate: noop,
  playlistId: 0,
  newPlaylistItems: [],
  playlist: null,
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  resetPlaylistItems: noop,
  saveNewPlaylistItems: () => Promise.resolve({}),
  resetAllState: noop,
  playlistLoading: false,
  updatePlaylistLoading: false
});
export const PlaylistItemSearchProvider = ({
  children,
  playlistId
}) => {
  const history = useHistory();
  const location = useLocation();
  const {
    saveNewPlaylistItems: savePlaylistItems,
    updatePlaylistLoading
  } = usePlaylistItemManager();
  const {
    playlist,
    playlistLoading
  } = useFullPlaylist(playlistId);
  const [query, setQuery] = useState(new URLSearchParams(window.location.search).get(SEARCH_QUERY_KEY) || '');
  const [newPlaylistItems, setPlaylistItems] = useState([]);
  useEffect(() => {
    if (playlist && playlist.items.length > 0) {
      const items = playlist.items.map(item => Object.assign({}, item, {
        hasBeenRemoved: false
      }));
      setPlaylistItems(items);
    }
  }, [playlist]);
  const debouncedNavigate = useMemo(() => debounce(history.push, 400), [history.push]);
  const debouncedUsageTrack = useMemo(() => debounce((searchQuery = '') => {
    UsageTracker.track('playlistInteraction', {
      action: 'search-items',
      searchQuery
    });
  }, 400), []);
  const setQueryAndNavigate = useCallback(newValue => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set(SEARCH_QUERY_KEY, newValue);
    setQuery(newValue);
    debouncedNavigate(Object.assign({}, location, {
      pathname: `${PLAYLIST_RESULTS_URL_FRAGMENT}`.replace(':playlistId', playlistId.toString()),
      search: searchParams.toString()
    }));
    debouncedUsageTrack(newValue);
  }, [debouncedNavigate, debouncedUsageTrack, location, playlistId]);
  const resetQuery = useCallback(() => {
    setQuery('');
  }, [setQuery]);
  const addPlaylistItem = useCallback(newOrUpdatedItem => {
    setPlaylistItems(items => produce(items, draft => {
      let foundItem = false;
      for (const item of draft) {
        if (item.itemId === newOrUpdatedItem.itemId && item.type === newOrUpdatedItem.type) {
          item.hasBeenRemoved = false;
          foundItem = true;
          break;
        }
      }
      if (!foundItem) draft.push(newOrUpdatedItem);
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'add-to-playlist',
      location: 'playlist-items-search'
    });
  }, []);
  const removePlaylistItem = useCallback(itemToRemove => {
    setPlaylistItems(items => produce(items, draft => {
      for (const item of draft) {
        if (item.itemId === itemToRemove.itemId && item.type === itemToRemove.type) {
          item.hasBeenRemoved = true;
          break;
        }
      }
    }));
    UsageTracker.track('playlistInteraction', {
      action: 'remove-item-from-playlist',
      location: 'playlist-items-search'
    });
  }, []);
  const resetPlaylistItems = useCallback(() => {
    setPlaylistItems(playlist ? playlist.items.map(item => Object.assign({}, item, {
      hasBeenRemoved: false
    })) : []);
  }, [playlist]);
  const resetAllState = useCallback(() => {
    resetPlaylistItems();
    resetQuery();
    UsageTracker.track('playlistInteraction', {
      action: 'cancel-item-changes'
    });
  }, [resetPlaylistItems, resetQuery]);
  const saveNewPlaylistItems = useCallback(() => {
    return savePlaylistItems(newPlaylistItems).then(result => {
      if (!(result !== null && result !== void 0 && result.errors)) {
        resetQuery();
      }
      return result;
    });
  }, [newPlaylistItems, resetQuery, savePlaylistItems]);
  const contextState = useMemo(() => ({
    query,
    setQueryAndNavigate,
    resetQuery,
    playlistId,
    newPlaylistItems,
    playlist,
    addPlaylistItem,
    removePlaylistItem,
    saveNewPlaylistItems,
    resetPlaylistItems,
    resetAllState,
    playlistLoading,
    updatePlaylistLoading
  }), [addPlaylistItem, newPlaylistItems, playlist, playlistId, query, removePlaylistItem, resetAllState, saveNewPlaylistItems, resetPlaylistItems, resetQuery, setQueryAndNavigate, playlistLoading, updatePlaylistLoading]);
  return /*#__PURE__*/_jsx(PlaylistItemSearchContext.Provider, {
    value: contextState,
    children: children
  });
};
export const usePlaylistItemSearch = () => {
  const contextState = useContext(PlaylistItemSearchContext);
  const {
    newPlaylistItems,
    playlist
  } = contextState;
  const itemsAddedCount = newPlaylistItems.filter(item => !item.hasBeenRemoved && !(playlist !== null && playlist !== void 0 && playlist.items.some(originalItem => originalItem.itemId === item.itemId && originalItem.type === item.type))).length;
  const itemsRemovedCount = newPlaylistItems.filter(item => item.hasBeenRemoved && (playlist === null || playlist === void 0 ? void 0 : playlist.items.some(originalItem => originalItem.itemId === item.itemId && originalItem.type === item.type))).length;
  return Object.assign({}, contextState, {
    numberOfSelectedItems: newPlaylistItems.filter(item => !item.hasBeenRemoved).length,
    itemsAddedCount,
    itemsRemovedCount
  });
};
export const PlaylistItemSearch = ({
  inputRef
}) => {
  const {
    query,
    setQueryAndNavigate
  } = usePlaylistItemSearch();
  const {
    isMobile
  } = useMediaQuery();
  const {
    loading: isLoading
  } = usePlaylistItemsSearch({
    skip: query.length < 2
  });
  const handleChange = useCallback(e => {
    setQueryAndNavigate(e.target.value);
  }, [setQueryAndNavigate]);
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FormControl, {
      "aria-label": I18n.text('learningCenter.savedItems.playlistDetails.search'),
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(SearchInput, {
        inputRef: inputRef,
        onChange: handleChange,
        placeholder: I18n.text('learningCenter.savedItems.playlistDetails.search'),
        value: query
      })
    }), !isMobile && /*#__PURE__*/_jsx(View, {
      inlineSize: "24px",
      marginInlineStart: "200",
      children: isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        margin: "0",
        size: "xs"
      })
    })]
  });
};
export const PlaylistItemSearchManager = ({
  listView,
  searchView
}) => {
  const {
    url
  } = useRouteMatch();
  const {
    playlistId
  } = usePlaylistItemSearch();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  return /*#__PURE__*/_jsxs(Switch, {
    children: [/*#__PURE__*/_jsxs(Route, {
      path: `*${PLAYLIST_ITEM_SEARCH_PATH}`,
      children: [userCanEditPlaylist !== null && !userCanEditPlaylist && /*#__PURE__*/_jsx(Redirect, {
        to: url
      }), searchView]
    }), /*#__PURE__*/_jsx(Route, {
      children: listView
    })]
  });
};
export const CancelPlaylistSearchButton = () => {
  const {
    url
  } = useRouteMatch();
  const {
    resetAllState
  } = usePlaylistItemSearch();
  return /*#__PURE__*/_jsx(RouterButton, {
    onClick: resetAllState,
    to: url.split(PLAYLIST_ITEM_SEARCH_PATH)[0],
    use: "secondary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.cancel"
    })
  });
};
export const SavePlaylistSearchButton = () => {
  const {
    url
  } = useRouteMatch();
  const navigate = useNavigate();
  const {
    saveNewPlaylistItems
  } = usePlaylistItemSearch();
  const handleClick = useCallback(() => {
    saveNewPlaylistItems().then(() => {
      navigate(url.split(PLAYLIST_ITEM_SEARCH_PATH)[0]);
    }).catch(() => {});
    UsageTracker.track('playlistInteraction', {
      action: 'save-item-changes'
    });
    /**
     * We have to add the logic for saving the playlist items added in search here
     */
  }, [navigate, saveNewPlaylistItems, url]);
  return /*#__PURE__*/_jsx(Button, {
    onClick: handleClick,
    use: "primary",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.common.save"
    })
  });
};